// Общие блоки страницы кабинета. Экран не задаёт своих отступов, шрифтов и цветов —
// только раскладывает эти блоки (docs/design/redesign-2026-10/05-decisions.md).
// Классы — из web/assets/css/components.css (перенос макета mockups/final).
import type { ReactNode } from "react";

import { Icon, type IconName } from "./Icon";

/** Заголовок экрана: один h1, не больше одной строки пояснения и одной главной кнопки. */
export function PageHeader({
  title,
  subtitle,
  actions,
  display = false,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Крупный заголовок брендовых экранов (первые шаги, вход). */
  display?: boolean;
}) {
  return (
    <header className={display ? "ph ph--display" : "ph"}>
      <div className="ph__text">
        <h1>{title}</h1>
        {subtitle && <p className="ph__sub">{subtitle}</p>}
      </div>
      {actions && <div className="ph__actions">{actions}</div>}
    </header>
  );
}

/** Секция страницы: заголовок h2 и, справа, тихое действие. */
export function Section({
  title,
  action,
  children,
  id,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section className="sec" aria-labelledby={id}>
      {(title || action) && (
        <div className="sec__head">
          {title && <h2 id={id}>{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** Плитка. Один вид на весь кабинет; hero — единственная яркая заливка экрана (Обзор). */
export function Tile({
  title,
  action,
  children,
  hero = false,
  flush = false,
  className = "",
}: {
  title?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  hero?: boolean;
  flush?: boolean;
  className?: string;
}) {
  const cls = ["tile", hero && "tile--hero", flush && "tile--flush", className].filter(Boolean).join(" ");
  return (
    <div className={cls}>
      {(title || action) && (
        <div className="tile__head">
          {title && <h2>{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export type StatusTone = "ok" | "warn" | "danger" | "info" | "neut";

/** Статус: точка + слово. Только цветом смысл не передаём (WCAG 1.4.1). */
export function Status({ tone, children, small = false }: { tone: StatusTone; children: ReactNode; small?: boolean }) {
  return <span className={`st st--${tone}${small ? " small" : ""}`}>{children}</span>;
}

/** Предметы пустых состояний (3D, docs/design/redesign-2026-10/_shared/3d). */
export type Art =
  | "overview" | "dialogs" | "leads" | "knowledge" | "scripts" | "channels" | "bell" | "search"
  | "start" | "testreply" | "agent-on" | "balance" | "queue" | "signpost" | "lock" | "hourglass" | "offline" | "gear";

export function ArtImage({ name, small = false }: { name: Art; small?: boolean }) {
  return (
    <div className={small ? "empty__art empty__art--sm" : "empty__art"}>
      <img src={`/assets/img/3d/empty-${name}-480.webp`} alt="" width={480} height={480} loading="lazy" decoding="async" />
    </div>
  );
}

/** Пустое состояние: предмет, что здесь будет, одно действие. */
export function EmptyState({
  art,
  title,
  text,
  action,
  flat = false,
}: {
  art?: Art;
  title: string;
  text?: ReactNode;
  action?: ReactNode;
  /** Внутри плитки или таблицы — без собственной рамки. */
  flat?: boolean;
}) {
  return (
    <div className={flat ? "empty empty--flat" : "empty"} role="status">
      {art && <ArtImage name={art} small={flat} />}
      <h2>{title}</h2>
      {text && <p>{text}</p>}
      {action}
    </div>
  );
}

/** Сообщение в блоке: предупреждение, ошибка, успех, справка. */
export function Banner({
  tone = "info",
  icon,
  children,
}: {
  tone?: "info" | "warn" | "danger" | "ok";
  icon?: IconName;
  children: ReactNode;
}) {
  const fallback: IconName = tone === "ok" ? "check" : tone === "info" ? "info" : "alert";
  return (
    <div className={`banner banner--${tone}`} role={tone === "danger" ? "alert" : "status"}>
      <Icon name={icon ?? fallback} />
      <div className="banner__body">{children}</div>
    </div>
  );
}

/** Чип-фильтр: плашка 28–32, цель нажатия 44. */
export function Chip({
  pressed,
  onClick,
  children,
  count,
}: {
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
  count?: number;
}) {
  return (
    <button type="button" className="chip" aria-pressed={pressed} onClick={onClick}>
      <span className="chip__in">
        {children}
        {count !== undefined && <span className="chip__n">{count}</span>}
      </span>
    </button>
  );
}
